import { Card, IReservation, ReservationUtils, StringUtils, TIME_FORMAT } from '@localina/core';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { GuestUtils } from '../../utils';

interface IProps {
    reservations: IReservation[];
}

const BUCKET_MINUTES = 15;
const DEFAULT_OCCUPANCY_SECONDS = 3600;

const MAX_ARRIVALS_SHOWN = 4;

interface IArrival {
    name: string;
    guests: number;
    minute: number;
}

interface IBucket {
    minute: number;

    guests: number;

    seated: number;

    arrivals: IArrival[];
}

interface IOccupancy {
    buckets: IBucket[];
    peakGuests: number;
    peakMinute: number;
    totalGuests: number;
    reservations: number;
}

const calculateOccupancy = (reservations: IReservation[], walkinLabel: string): IOccupancy | null => {
    const live = reservations.filter((reservation) => !ReservationUtils.isReservationInDeadStatus(reservation));
    if (!live.length) {
        return null;
    }

    const spans = live.map((reservation) => {
        const start = DateTime.fromISO(reservation.reservationDateTime).toLocal();
        const startMinute = start.hour * 60 + start.minute;
        return {
            startMinute,
            endMinute: startMinute + (reservation.occupancyTime || DEFAULT_OCCUPANCY_SECONDS) / 60,
            guests: reservation.participants,
            name: GuestUtils.formatGuestName(reservation.guestInfo, walkinLabel),
        };
    });

    const floorToBucket = (minute: number) => Math.floor(minute / BUCKET_MINUTES) * BUCKET_MINUTES;
    const from = floorToBucket(Math.min(...spans.map((span) => span.startMinute)));
    const to = floorToBucket(Math.max(...spans.map((span) => span.endMinute)));

    const buckets: IBucket[] = [];
    let peakGuests = 0;
    let peakMinute = from;
    for (let minute = from; minute <= to; minute += BUCKET_MINUTES) {
        const covering = spans.filter((span) => minute >= span.startMinute && minute < span.endMinute);
        const guests = covering.reduce((total, span) => total + span.guests, 0);
        const arrivals = spans
            .filter((span) => span.startMinute >= minute && span.startMinute < minute + BUCKET_MINUTES)
            .map((span) => ({ name: span.name, guests: span.guests, minute: span.startMinute }))
            .sort((a, b) => a.minute - b.minute);
        buckets.push({ minute, guests, seated: covering.length, arrivals });
        if (guests > peakGuests) {
            peakGuests = guests;
            peakMinute = minute;
        }
    }

    while (buckets.length > 1 && buckets[buckets.length - 1].guests === 0) {
        buckets.pop();
    }

    return {
        buckets,
        peakGuests,
        peakMinute,
        totalGuests: live.reduce((total, reservation) => total + reservation.participants, 0),
        reservations: live.length,
    };
};

const formatMinute = (minute: number) =>
    DateTime.fromObject({ hour: Math.floor(minute / 60) % 24, minute: minute % 60 }).toFormat(TIME_FORMAT);

export const ReservationsOccupancy: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const walkinLabel = t('reservations.view.fields.walkin');

    const occupancy = React.useMemo(
        () => calculateOccupancy(props.reservations, walkinLabel),
        [props.reservations, walkinLabel],
    );

    const [hovered, setHovered] = React.useState<number | null>(null);
    const [pinned, setPinned] = React.useState<number | null>(null);
    const [detailLeft, setDetailLeft] = React.useState(50);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const slotRefs = React.useRef<Map<number, HTMLButtonElement>>(new Map());

    React.useEffect(() => {
        setPinned(null);
        setHovered(null);
    }, [props.reservations]);

    React.useEffect(() => {
        if (pinned === null) {
            return;
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setPinned(null);
            }
        };
        const onPointerDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setPinned(null);
            }
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [pinned]);

    const activeMinute = hovered ?? pinned;

    React.useLayoutEffect(() => {
        if (activeMinute === null) {
            return;
        }
        const slot = slotRefs.current.get(activeMinute);
        const parent = slot?.offsetParent as HTMLElement | null;
        if (!slot || !parent) {
            return;
        }
        const center = slot.offsetLeft + slot.offsetWidth / 2;
        setDetailLeft((center / parent.clientWidth) * 100);
    }, [activeMinute]);

    if (!occupancy) {
        return null;
    }

    const active = occupancy.buckets.find((bucket) => bucket.minute === activeMinute);
    const describe = (bucket: IBucket) =>
        `${formatMinute(bucket.minute)}–${formatMinute(bucket.minute + BUCKET_MINUTES)}, ` +
        `${t('reservations.view.countOfGuestsAtExactTime.label', { count: bucket.guests })}, ` +
        `${t('reservations.occupancy.arrivals', { count: bucket.arrivals.length })}`;

    return (
        <Card extraClassName="reservations-occupancy">
            <div ref={containerRef}>
                <div className="reservations-occupancy__head">
                    <b className="reservations-occupancy__totals">
                        {t('reservations.view.countOfGuestsAtExactTime.label', { count: occupancy.totalGuests })}
                        {' · '}
                        {t('reservations.occupancy.reservationsCount', { count: occupancy.reservations })}
                    </b>
                    <span className="reservations-occupancy__busiest">
                        {t('reservations.occupancy.busiest', { time: formatMinute(occupancy.peakMinute) })}
                    </span>
                </div>

                {}
                <div className="reservations-occupancy__strip" onMouseLeave={() => setHovered(null)}>
                    {occupancy.buckets.map((bucket) => {
                        const isActive = bucket.minute === activeMinute;
                        return (
                            <button
                                key={bucket.minute}
                                type="button"
                                ref={(el) => {
                                    if (el) {
                                        slotRefs.current.set(bucket.minute, el);
                                    } else {
                                        slotRefs.current.delete(bucket.minute);
                                    }
                                }}
                                className={StringUtils.combineStrings([
                                    'reservations-occupancy__strip__slot',
                                    bucket.minute === occupancy.peakMinute && 'is-peak',
                                    isActive && 'is-active',
                                    pinned === bucket.minute && 'is-pinned',
                                ])}
                                aria-label={describe(bucket)}
                                aria-pressed={pinned === bucket.minute}
                                onMouseEnter={() => setHovered(bucket.minute)}
                                onFocus={() => setHovered(bucket.minute)}
                                onBlur={() => setHovered(null)}
                                onClick={() =>
                                    setPinned((current) => (current === bucket.minute ? null : bucket.minute))
                                }
                            >
                                <span
                                    className="reservations-occupancy__strip__bar"
                                    style={{ height: `${Math.round((bucket.guests / occupancy.peakGuests) * 100)}%` }}
                                />
                            </button>
                        );
                    })}
                </div>

                {}
                <div className="reservations-occupancy__axis" aria-hidden="true">
                    <span>{formatMinute(occupancy.buckets[0].minute)}</span>
                    <span>{formatMinute(occupancy.buckets[occupancy.buckets.length - 1].minute)}</span>
                </div>

                {Boolean(active) && (
                    <div
                        className="reservations-occupancy__detail"
                        style={{
                            left: `${Math.min(88, Math.max(12, detailLeft))}%`,
                        }}
                        role="status"
                    >
                        <div className="reservations-occupancy__detail__time">
                            {formatMinute(active!.minute)}
                            <span className="reservations-occupancy__detail__time__to">
                                {' – '}
                                {formatMinute(active!.minute + BUCKET_MINUTES)}
                            </span>
                        </div>
                        <div className="reservations-occupancy__detail__counts">
                            {t('reservations.view.countOfGuestsAtExactTime.label', { count: active!.guests })}
                            {' · '}
                            {t('reservations.occupancy.reservationsCount', { count: active!.seated })}
                        </div>
                        {active!.arrivals.length > 0 ? (
                            <ul className="reservations-occupancy__detail__arrivals">
                                {active!.arrivals.slice(0, MAX_ARRIVALS_SHOWN).map((arrival, index) => (
                                    <li key={`${arrival.name}-${index}`}>
                                        <span className="reservations-occupancy__detail__arrivals__time">
                                            {formatMinute(arrival.minute)}
                                        </span>
                                        <span className="reservations-occupancy__detail__arrivals__name">
                                            {arrival.name}
                                        </span>
                                        <span className="reservations-occupancy__detail__arrivals__guests">
                                            {arrival.guests}
                                        </span>
                                    </li>
                                ))}
                                {active!.arrivals.length > MAX_ARRIVALS_SHOWN && (
                                    <li className="reservations-occupancy__detail__arrivals__more">
                                        {t('reservations.occupancy.andMore', {
                                            count: active!.arrivals.length - MAX_ARRIVALS_SHOWN,
                                        })}
                                    </li>
                                )}
                            </ul>
                        ) : (
                            <div className="reservations-occupancy__detail__none">
                                {t('reservations.occupancy.noArrivals')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};
